import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';
import {
  AuthUser,
  LoginRequest,
  LoginResponse,
  MeResponse
} from '../../../shared/models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly apiUrl = environment.apiUrl;

  private readonly tokenKey = 'prealty_token';
  private readonly userUuidKey = 'prealty_user_uuid';
  private readonly userKey = 'prealty_user';

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  // =====================================================
  // LOGIN
  // =====================================================

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${this.apiUrl}/auth/login`,
      credentials
    ).pipe(
      tap(response => {
        if (
          response.success &&
          response.data?.token &&
          response.data?.user?.uuid
        ) {
          const remember = credentials.remember ?? false;

          this.setAuthData(
            response.data.token,
            response.data.user.uuid,
            remember
          );

          this.setUser(
            response.data.user,
            remember
          );
        }
      })
    );
  }

  // =====================================================
  // ME
  // =====================================================

  me(): Observable<MeResponse> {
    return this.http.get<MeResponse>(
      `${this.apiUrl}/auth/me`
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.setUser(
            response.data,
            this.isPersistentSession()
          );
        }
      })
    );
  }

  // =====================================================
  // LOGOUT
  // =====================================================

  logout(): Observable<unknown> {
    return this.http.post(
      `${this.apiUrl}/auth/logout`,
      {}
    ).pipe(
      finalize(() => {
        this.clearSession();
        this.router.navigate(['/login']);
      })
    );
  }

  // =====================================================
  // SESSION STORAGE
  // =====================================================

  private setAuthData(
    token: string,
    userUuid: string,
    remember: boolean
  ): void {
    this.clearSession();

    const storage = remember
      ? localStorage
      : sessionStorage;

    storage.setItem(
      this.tokenKey,
      token
    );

    storage.setItem(
      this.userUuidKey,
      userUuid
    );
  }

  private setUser(
    user: AuthUser,
    remember: boolean
  ): void {
    const storage = remember
      ? localStorage
      : sessionStorage;

    storage.setItem(
      this.userKey,
      JSON.stringify(user)
    );
  }

  private isPersistentSession(): boolean {
    return localStorage.getItem(this.tokenKey) !== null;
  }

  // =====================================================
  // CLEAR SESSION
  // =====================================================

  clearSession(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userUuidKey);
    localStorage.removeItem(this.userKey);

    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userUuidKey);
    sessionStorage.removeItem(this.userKey);
  }

  // =====================================================
  // TOKEN
  // =====================================================

  getToken(): string | null {
    return (
      localStorage.getItem(this.tokenKey) ??
      sessionStorage.getItem(this.tokenKey)
    );
  }

  // =====================================================
  // USER UUID
  // =====================================================

  getUserUuid(): string | null {
    return (
      localStorage.getItem(this.userUuidKey) ??
      sessionStorage.getItem(this.userUuidKey)
    );
  }

  // =====================================================
  // AUTHENTICATED
  // =====================================================

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  // =====================================================
  // USER
  // =====================================================

  getUser(): AuthUser | null {
    const storedUser =
      localStorage.getItem(this.userKey) ??
      sessionStorage.getItem(this.userKey);

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser) as AuthUser;
    } catch {
      this.clearSession();
      return null;
    }
  }

  // =====================================================
  // ROLE
  // =====================================================

  getRole(): string | null {
    return this.getUser()?.role ?? null;
  }

  hasRole(role: string): boolean {
    return this.getRole() === role;
  }

  hasAnyRole(roles: string[]): boolean {
    const currentRole = this.getRole();

    if (!currentRole) {
      return false;
    }

    return roles.includes(currentRole);
  }

  // =====================================================
  // STATUS
  // =====================================================

  getStatus(): string | null {
    return this.getUser()?.status ?? null;
  }

  isActive(): boolean {
    return this.getStatus() === 'active';
  }

}